import { ApiError } from '@/shared/api/client';

import type { PendingIdentity, PendingMfaState, PendingSessionPayload } from '@/shared/types/mfa';

/**
 * Reading MFA out of a session payload, defensively.
 *
 * A payload with no `mfa` at all is a server that predates MFA or an account it
 * does not concern: the session is complete. Anything the client cannot read
 * as `challenge` or `enrol` is not pending — the server still refuses every
 * privileged call from a pending session, so the client never has to be right
 * about more than which screen to show.
 */
export function pendingMfaState(payload: unknown): PendingMfaState | null {
  if (payload === null || typeof payload !== 'object') return null;

  const mfa = (payload as { mfa?: unknown }).mfa;

  if (mfa === null || typeof mfa !== 'object') return null;

  const state = (mfa as { state?: unknown }).state;

  return state === 'challenge' || state === 'enrol' ? state : null;
}

/** The MFA step a refusal sends the session back to, if it is one. */
export function mfaStateFromError(error: unknown): PendingMfaState | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'MFA_CHALLENGE_REQUIRED') return 'challenge';
  if (error.code === 'MFA_ENROLMENT_REQUIRED') return 'enrol';

  return null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Who is signing in: name, address and language — and nothing else.
 *
 * Accepts the tenant shape (`{ user: { display_name, email, locale } }`) and
 * the operator's flat profile (`{ name, email }`), so the pending screen can
 * say whose second step it is without holding anything privileged.
 */
export function pendingIdentityOf(payload: unknown): PendingIdentity | null {
  if (payload === null || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const user =
    record.user !== null && typeof record.user === 'object'
      ? (record.user as Record<string, unknown>)
      : record;

  const identity = {
    display_name: text(user.display_name) ?? text(user.name),
    email: text(user.email),
    locale: text(user.locale),
  };

  return identity.display_name || identity.email ? identity : null;
}

/**
 * A pending session as the client keeps it.
 *
 * Whatever else the server put beside `mfa` — sign-in answers with the full
 * payload shape — is dropped: a pending session holds no permissions, roles,
 * scope or organisation settings in the client's cache, so nothing can render
 * them by mistake.
 */
export function toPendingPayload(payload: unknown, state: PendingMfaState): PendingSessionPayload {
  const mfa =
    payload !== null && typeof payload === 'object'
      ? (payload as { mfa?: Record<string, unknown> }).mfa
      : undefined;

  return {
    mfa: {
      state,
      required: typeof mfa?.required === 'boolean' ? mfa.required : undefined,
      enabled: typeof mfa?.enabled === 'boolean' ? mfa.enabled : undefined,
    },
    user: pendingIdentityOf(payload),
  };
}

/**
 * Router state (never the URL) asking the security page to open the
 * authenticator replacement, confirmed with a recovery code — the way on for
 * somebody who has just signed in with one because their phone is gone.
 */
export const REPLACE_LOST_AUTHENTICATOR = { mfaIntent: 'replace-lost-authenticator' } as const;
